import React, { useCallback, useEffect, useState } from 'react';
import { Trash2, X, RotateCcw, FileText, Loader2 } from 'lucide-react';
import { documentService } from '../../services/documentService';
import { useLanguage } from '../../i18n';
import { retentionDays, readRetentionPolicy } from '../../utils/retentionPolicy';
import { formatLocalDate } from '../../utils/datetime';
import Modal from '../ui/Modal';

/**
 * Recently Deleted (#126/#127) — the document "trash" staging view, opened from
 * the File Manager's three-dot menu. On open it asks the BE for the trashed docs,
 * passing the active retention policy so the BE auto-purges anything past its
 * window first (so expired files are cleaned up exactly when this view loads).
 * Each row offers Restore (back to its original folder) and Delete Permanently.
 */
const RecentlyDeletedModal = ({ onClose, onChanged }) => {
    const { t } = useLanguage();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const days = retentionDays(readRetentionPolicy()); // drives the auto-purge
            const res = await documentService.listTrash(days);
            setDocs(res?.documents || []);
        } catch (_) {
            setDocs([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleRestore = useCallback(async (id) => {
        setBusyId(id);
        try {
            await documentService.restoreDocument(id);
            setDocs((prev) => prev.filter((d) => d.id !== id));
            try { window.dispatchEvent(new CustomEvent('omnis:document-deleted', { detail: { id } })); } catch (_) { /* no-op */ }
            onChanged?.();
        } catch (_) { /* leave the row */ } finally {
            setBusyId(null);
        }
    }, [onChanged]);

    const handlePurge = useCallback(async (id) => {
        setBusyId(id);
        try {
            await documentService.deleteDocument(id); // permanent
            setDocs((prev) => prev.filter((d) => d.id !== id));
        } catch (_) { /* leave the row */ } finally {
            setBusyId(null);
        }
    }, []);

    return (
        <Modal onClose={onClose} zIndex="z-[80]" size="lg" layout="column" cardClassName="max-h-[80dvh]" cardTestId="recently-deleted-modal">
                <div className="flex items-center justify-between px-6 py-4 border-b border-pai-hairline">
                    <div className="flex items-center gap-3">
                        <Trash2 className="text-pai-meta" size={20} />
                        <h3 className="text-[19px] font-bold text-white">{t('recentlyDeleted')}</h3>
                    </div>
                    <button onClick={onClose} className="pai-icon-btn w-[38px] h-[38px]" aria-label={t('commonClose', 'Close')}>
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-white/40">
                            <Loader2 size={24} className="animate-spin" />
                        </div>
                    ) : docs.length === 0 ? (
                        <div data-testid="recently-deleted-empty" className="py-12 text-center text-[14.5px] text-white/40">
                            {t('recentlyDeletedEmpty')}
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {docs.map((d) => (
                                <li
                                    key={d.id}
                                    data-testid="recently-deleted-row"
                                    data-doc-id={d.id}
                                    className="flex items-center gap-3 px-4 py-3 rounded-row border border-pai-subtle bg-white/[0.025] transition-colors hover:bg-white/[0.05]"
                                >
                                    <FileText size={18} className="text-white/40 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[14.5px] font-semibold text-white truncate" title={d.original_filename}>
                                            {d.original_filename}
                                        </div>
                                        <div className="text-[12.5px] text-pai-meta truncate tnum">
                                            {d.folder_name ? `${d.folder_name} · ` : ''}
                                            {d.deleted_at ? formatLocalDate(d.deleted_at) : ''}
                                        </div>
                                    </div>
                                    <button
                                        data-testid="recently-deleted-restore"
                                        onClick={() => handleRestore(d.id)}
                                        disabled={busyId === d.id}
                                        title={t('restore')}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-pai-green hover:bg-pai-green/[0.12] rounded-chip transition-colors disabled:opacity-50"
                                    >
                                        <RotateCcw size={14} /> {t('restore')}
                                    </button>
                                    <button
                                        data-testid="recently-deleted-purge"
                                        onClick={() => handlePurge(d.id)}
                                        disabled={busyId === d.id}
                                        title={t('deletePermanently')}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-pai-red hover:bg-pai-red/[0.12] rounded-chip transition-colors disabled:opacity-50"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </Modal>
    );
};

export default RecentlyDeletedModal;
